// The notices above the create form about the DRAFT rather than the ticket
// (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md, "Saving, syncing, and
// failure", "Reopening an old draft", "Release day"). Presentation only: what each says is
// decided in draft-flow.js (`syncNotice`, `staleChoicesFor`) and by pages/tickets/New.jsx.
//
//   notice    a draft-flow.js notice: offline, refused, gone, taken, filed, discarded.
//             `stale` is not drawn here: the draft is reloaded and `unsaved` says it.
//   unsaved   `{ heading, text }`: typing that could not be saved, shown ONCE (it is kept
//             nowhere else), with a Dismiss.
//   legacy    the old browser slot's draft (legacy-slot.js), offered as Keep / Discard. Keep
//             is aria-disabled until `legacyReady` (draft-screen.js `legacyKeepReady`).
//   findings  stale-config.js findings: blocking ones must be resolved before Create; the
//             rest say a value will not be filed.
//   wasSaved  the draft existed on the server when this screen opened; only then does the
//             blocking heading say it was started before the setup changed.
//
// Tones follow the screen's existing bands: amber for a page-level notice (the shell's
// dev-stub band in web/shell/PlatformLayout.jsx, never the note tokens, which mean
// "internal note"), the explicit red pair
// for a problem (List.jsx's error card), sunken for plain information.

import { Link } from 'react-router';
import { supportPath } from '../../support-path.js';
import { staleHeading } from './draft-flow.js';

// Complete class strings, so Tailwind's source scanner sees them.
const TONES = {
  info: 'border-line-strong bg-sunken text-ink-secondary',
  warn: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500 dark:bg-amber-900 dark:text-amber-100',
  problem: 'border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200',
};

const BAND = 'mt-3 rounded-md border px-3 py-2 text-sm';

export default function DraftNotices({
  notice, unsaved, onDismissUnsaved, legacy, legacyReady, onKeepLegacy, onDiscardLegacy, findings = [], noticeRef,
  wasSaved = false,
}) {
  const blocking = findings.filter((finding) => finding.blocking);
  const dropped = findings.filter((finding) => !finding.blocking);

  return (
    <>
      {/* Polite: a save going offline, or the draft closing elsewhere, is read out. */}
      <div aria-live="polite">
        {notice && notice.kind !== 'stale' && (
          // `noticeRef` and tabIndex -1: the screen moves focus here when the form turns
          // read-only, rather than letting it fall to <body> from a disabled control.
          <p ref={noticeRef} tabIndex={-1} className={`${BAND} ${TONES[notice.tone] ?? TONES.info}`}>
            {notice.message}
            {notice.publicRef && (
              <>
                {' '}
                <Link to={supportPath(`/tickets/${notice.publicRef}`)} className="font-medium underline">
                  Open ticket #{notice.publicRef}
                </Link>
              </>
            )}
            {notice.readOnly && ' This form can no longer be saved.'}
          </p>
        )}
      </div>

      {unsaved && (
        <div className={`${BAND} ${TONES.warn}`}>
          <div className="flex items-start justify-between gap-3">
            <p className="font-medium">{unsaved.heading}</p>
            <button type="button" className="shrink-0 font-medium hover:underline" onClick={onDismissUnsaved}>
              Dismiss
            </button>
          </div>
          {unsaved.text && (
            <>
              {/* Shown once and kept nowhere: copy what is needed before dismissing. */}
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded border border-line bg-surface p-2 font-sans text-ink">
                {unsaved.text}
              </pre>
              <p className="mt-1 text-xs">This text is not kept anywhere else. Copy what you need before dismissing it.</p>
            </>
          )}
        </div>
      )}

      {legacy && (
        <div className={`${BAND} ${TONES.warn} flex flex-wrap items-center justify-between gap-3`}>
          <span>
            This browser still holds an unfinished ticket from the old create screen
            {legacy.saved_at ? `, saved ${new Date(legacy.saved_at).toLocaleString()}` : ''}. It may be
            someone else&apos;s if this desk is shared. Keep it as this draft, or discard it?
          </span>
          <span className="flex gap-3">
            <button
              type="button"
              className="font-medium hover:underline"
              aria-disabled={!legacyReady}
              title={legacyReady ? undefined : 'Available once the forms and queues have loaded'}
              onClick={onKeepLegacy}
            >
              Keep
            </button>
            <button type="button" className="font-medium hover:underline" onClick={onDiscardLegacy}>
              Discard
            </button>
          </span>
        </div>
      )}

      {blocking.length > 0 && (
        <div className={`${BAND} ${TONES.problem}`}>
          <p className="font-medium">{staleHeading({ wasSaved })}</p>
          <ul className="mt-1 list-inside list-disc">
            {blocking.map((finding) => <li key={finding.field}>{finding.message}</li>)}
          </ul>
        </div>
      )}

      {dropped.length > 0 && (
        <div className={`${BAND} ${TONES.info}`}>
          <ul className="list-inside list-disc">
            {dropped.map((finding) => <li key={finding.field}>{finding.message}</li>)}
          </ul>
        </div>
      )}
    </>
  );
}
